import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, IconAnimation, IconColor, IconName, IconSize } from '@as/cotopaxi';
import { mergeObjectDeep } from '@as-react/common-utils';
// "react-leaflet" does not work with SSR, we must use dynamic imports
const loadLeaflet = (leaflet) => __awaiter(void 0, void 0, void 0, function* () {
    const reactLeaflet = yield import('react-leaflet');
    leaflet(reactLeaflet);
});
const loadLeafletIcon = (leafletIcon) => __awaiter(void 0, void 0, void 0, function* () {
    const reactLeaflet = yield import('leaflet');
    leafletIcon(reactLeaflet);
});
const loadReactDOMServer = (DOMServer) => __awaiter(void 0, void 0, void 0, function* () {
    const reactDOMServer = yield import('react-dom/server');
    DOMServer(reactDOMServer);
});
const getFasciaIconUrl = (url, mainWebShop, iconName) => url.replace('{mainWebShop}', mainWebShop).replace('{iconName}', iconName);
const ZOOM = 6;
export const SEARCH_LOCATION_ID = 'SEARCH_LOCATION_ID';
export var OpenStreetIcons;
(function (OpenStreetIcons) {
    OpenStreetIcons["IN_STOCK"] = "instock";
    OpenStreetIcons["OUT_STOCK"] = "outstock";
    OpenStreetIcons["DEFAULT"] = "default";
})(OpenStreetIcons || (OpenStreetIcons = {}));
const defaultIconConfig = {
    fasciaIcon: {
        iconAnchor: [30, 45],
        iconUrl: '/content/dam/{mainWebShop}/open-street-map-icons/{iconName}.png',
    },
    baseIcon: { iconSize: [30, 30], iconAnchor: [15, 30] },
};
export const Map = ({ active, containerStyles = null, isOpen, markers, focusOnMarkers, mapCenter, defaultBounds = null, showFasciaIcons, showStock, activePointId, keyboard = false, onMarkerClick, noMarkersAnimation, markerIconConfigOverrides, }) => {
    const [leaflet, setLeaflet] = useState(null);
    const [leafletIcon, setLeafletIcon] = useState(null);
    const [reactDOMServe, setReactDOMServe] = useState(null);
    const [activeMarkerId, setActiveMarkerId] = useState(null);
    const [zoom, setZoom] = useState(ZOOM);
    const [boundsFeatureGroup, setBoundsFeatureGroup] = useState(defaultBounds);
    const [mapInitialized, setIsMapInitialized] = useState(false);
    const mapRefElement = useRef(null);
    const markersRefElements = useRef({});
    const markerIconConfig = mergeObjectDeep(defaultIconConfig, markerIconConfigOverrides || {});
    useEffect(() => {
        loadLeaflet(setLeaflet);
        loadLeafletIcon(setLeafletIcon);
        loadReactDOMServer(setReactDOMServe);
    }, []);
    useEffect(() => {
        var _a;
        if (active && ((_a = mapRefElement.current) === null || _a === void 0 ? void 0 : _a.leafletElement) && boundsFeatureGroup) {
            mapRefElement.current.leafletElement.fitBounds(boundsFeatureGroup);
        }
    }, [active, boundsFeatureGroup]);
    useEffect(() => {
        !isOpen && setActiveMarkerId(null);
        if (mapRefElement.current) {
            mapRefElement.current.leafletElement.invalidateSize();
        }
    }, [isOpen]);
    useEffect(() => {
        var _a;
        if (!activePointId) {
            setActiveMarkerId(null);
        }
        else if (activePointId && activePointId !== activeMarkerId) {
            const activeMarker = markers.find(marker => marker.id === activePointId);
            const latitude = activeMarker.latitude;
            const longitude = activeMarker.longitude;
            (_a = mapRefElement.current) === null || _a === void 0 ? void 0 : _a.leafletElement.flyTo([latitude, longitude]);
            setActiveMarkerId(activePointId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePointId, markers]);
    const groupRef = useCallback((node) => {
        var _a, _b, _c, _d;
        if (node && markers.length && !(focusOnMarkers === null || focusOnMarkers === void 0 ? void 0 : focusOnMarkers.length)) {
            setBoundsFeatureGroup(node.leafletElement.getBounds());
        }
        else if (focusOnMarkers === null || focusOnMarkers === void 0 ? void 0 : focusOnMarkers.length) {
            const bounds = focusOnMarkers.map(marker => [marker.latitude, marker.longitude]);
            (_b = (_a = mapRefElement.current) === null || _a === void 0 ? void 0 : _a.leafletElement) === null || _b === void 0 ? void 0 : _b.closePopup();
            setBoundsFeatureGroup(bounds);
            // open popup for the nearest focus marker
            if (focusOnMarkers[0].openPopup && mapInitialized) {
                (_d = (_c = markersRefElements.current[focusOnMarkers[0].id]) === null || _c === void 0 ? void 0 : _c.leafletElement) === null || _d === void 0 ? void 0 : _d.openPopup();
            }
        }
        else if (!markers.length && defaultBounds) {
            setBoundsFeatureGroup(defaultBounds);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markers, defaultBounds, focusOnMarkers, mapInitialized]);
    if (!leaflet || !leafletIcon || !reactDOMServe) {
        return null;
    }
    const { Map: MapContainer, TileLayer, Marker, FeatureGroup: FeatureGroupContainer, Popup } = leaflet;
    const getIcon = (available, activeItem, isSearchLocation, storeMainWebShop) => {
        var _a, _b, _c, _d, _e;
        return showFasciaIcons && !isSearchLocation
            ? leafletIcon.icon({
                className: activeItem && !noMarkersAnimation ? 'as-a-image--animation-bop-cpu' : '',
                iconAnchor: (_a = markerIconConfig.fasciaIcon) === null || _a === void 0 ? void 0 : _a.iconAnchor,
                iconSize: (_b = markerIconConfig.fasciaIcon) === null || _b === void 0 ? void 0 : _b.iconSize,
                iconUrl: getFasciaIconUrl((_c = markerIconConfig.fasciaIcon) === null || _c === void 0 ? void 0 : _c.iconUrl, storeMainWebShop, !showStock ? OpenStreetIcons.DEFAULT : available ? OpenStreetIcons.IN_STOCK : OpenStreetIcons.OUT_STOCK),
            })
            : leafletIcon.divIcon({
                className: '',
                iconAnchor: (_d = markerIconConfig === null || markerIconConfig === void 0 ? void 0 : markerIconConfig.baseIcon) === null || _d === void 0 ? void 0 : _d.iconAnchor,
                iconSize: (_e = markerIconConfig === null || markerIconConfig === void 0 ? void 0 : markerIconConfig.baseIcon) === null || _e === void 0 ? void 0 : _e.iconSize,
                html: reactDOMServe.renderToString(_jsx(Icon, { name: IconName.MAP_MARKER, size: IconSize.EXTRA_MAJOR, animation: activeItem && !noMarkersAnimation ? IconAnimation.BOP : null, color: !showStock ? IconColor.DEFAULT : available ? IconColor.POSITIVE : IconColor.NOTICE })),
            });
    };
    const handleMarkerClick = (event, id, coordinates) => {
        event.originalEvent.stopImmediatePropagation();
        setActiveMarkerId(id);
        onMarkerClick === null || onMarkerClick === void 0 ? void 0 : onMarkerClick(id);
        mapRefElement.current.leafletElement.flyTo(coordinates);
    };
    const mapMarkersCallback = ({ id, longitude, latitude, available, storeMainWebShop, popupContent }) => (_jsx(Marker, { ref: el => (markersRefElements.current[id] = el), icon: getIcon(available, id === activeMarkerId, id === SEARCH_LOCATION_ID, storeMainWebShop), keyboard: false, onClick: (event) => handleMarkerClick(event, id, [latitude, longitude]), position: [latitude, longitude], children: popupContent && _jsx(Popup, { autoPan: false, children: popupContent }) }, id));
    return (_jsxs(MapContainer, { style: containerStyles, scrollWheelZoom: false, center: mapCenter, keyboard: keyboard, ref: mapRefElement, bounds: defaultBounds, whenReady: () => setIsMapInitialized(true), onZoomEnd: () => setZoom(mapRefElement.current.leafletElement.getZoom()), zoom: zoom, children: [_jsx(TileLayer, { detectRetina: true, url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "\u00A9 <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors" }), _jsx(FeatureGroupContainer, { ref: groupRef, children: markers.map(mapMarkersCallback) })] }));
};
